import { getPriceFromDeal } from '../../sharedModules/getPriceFromDeal';
import { ApiProductType } from '../../types/ProductTypes';
import type { Deal } from '../types/Deal';
import type { Translate } from '../types/Localiser';
import getCurrencySymbol from '../utils/getCurrencySymbol';

type SubscriptionPricing = {
  yearlyPrice: number;
  monthlyPrice: number;
};

const getSubscriptionPricing = (deal: Deal): SubscriptionPricing => ({
  yearlyPrice: Number(getPriceFromDeal(deal, 'yearly')) || 0,
  monthlyPrice: Number(getPriceFromDeal(deal, 'monthly')) || 0,
});

const getLinkText = (deal: Deal, translate: Translate): string => {
  // Use link text from the API if set, fallback to default link text (should never be needed)
  if (deal.offer.link_text) {
    return deal.offer.link_text;
  }
  return translate('defaultLinkText');
};

const isSubscriptionDeal = (
  deal: Deal,
  { yearlyPrice, monthlyPrice }: SubscriptionPricing,
): boolean => {
  return deal.product_type === ApiProductType.subscription && (yearlyPrice > 0 || monthlyPrice > 0);
};

const getSubscriptionMerchantLinkText = (
  merchantName: string | undefined,
  translate: Translate,
  deal: Deal,
  { yearlyPrice, monthlyPrice }: SubscriptionPricing,
): string => {
  if (!merchantName) {
    return translate('defaultLinkText');
  }

  const currencySymbol = getCurrencySymbol(deal.offer.currency_iso);
  const yearlyCTA = `${currencySymbol}${yearlyPrice}/year ${translate('priceLinkAtMerchant', [
    merchantName,
  ])}`;

  const monthlyCTA = `${currencySymbol}${monthlyPrice}/mth ${translate('priceLinkAtMerchant', [
    merchantName,
  ])}`;

  return yearlyPrice > 0 ? yearlyCTA : monthlyCTA;
};

const isSubscriptionFreeTrial = (
  deal: Deal,
  { yearlyPrice, monthlyPrice }: SubscriptionPricing,
): boolean => {
  return (
    deal.product_type === ApiProductType.subscription && yearlyPrice === 0 && monthlyPrice === 0
  );
};

const getFreeTrialMerchantLinkText = (
  merchantName: string | undefined,
  translate: Translate,
): string => {
  if (!merchantName) {
    return translate('freeTrial');
  }

  return `${translate('freeTrial')} ${translate('priceLinkAtMerchant', [merchantName])}`;
};

const getMerchantLinkText = (deal: Deal, translate: Translate, isPriceEnabled: boolean): string => {
  const merchantName = deal.merchant.name ?? '';
  const subscriptionPricing = getSubscriptionPricing(deal);

  if (isSubscriptionFreeTrial(deal, subscriptionPricing)) {
    return getFreeTrialMerchantLinkText(merchantName, translate);
  }

  if (isSubscriptionDeal(deal, subscriptionPricing)) {
    return getSubscriptionMerchantLinkText(merchantName, translate, deal, subscriptionPricing);
  }

  if (deal.offer.merchant_link_text) {
    return deal.offer.merchant_link_text;
  }

  if (deal.offer.price && isPriceEnabled) {
    return translate('priceLinkAtMerchant', [merchantName]);
  }

  return translate('defaultMerchantLinkText', [merchantName]);
};

export default (
  deal: Deal,
  displayMerchant: boolean | undefined,
  translate: Translate,
  isPriceEnabled = false,
): string => {
  const linkText = getLinkText(deal, translate);
  if (displayMerchant) {
    return getMerchantLinkText(deal, translate, isPriceEnabled);
  }
  return linkText;
};
